"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export interface BlogPostTranslation {
    id: number;
    posts_slug: string;
    languages_code: string;
    content: string;
    title: string;
    caption: string;
}

export function BlogCard({ post, index = 0 }: { post: BlogPostTranslation; index?: number; }) {

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: 0.5,
                delay: index * 0.15, // Sequential delay based on index
            }}
            className="w-full h-full p-1 xs:p-2 sm:p-3"
        >
            <Link href={`/blog/${post.posts_slug}`} className="h-full">
                <Card className="h-full w-full flex flex-col transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer">
                    <CardHeader className="pb-2 flex-shrink-0">
                        <div className="text-sm text-muted-foreground mb-1">{post.languages_code.toUpperCase()}</div>
                        <h2 className="text-2xl font-bold tracking-tight line-clamp-2 h-[60px]">{post.title}</h2>
                    </CardHeader>
                    <CardContent className="flex-grow overflow-hidden">
                        <p className="text-muted-foreground font-medium mb-3 line-clamp-2">{post.caption}</p>
                        <p className="text-sm line-clamp-4">{post.content}</p>
                    </CardContent>
                    <CardFooter className="mt-auto flex-shrink-0">
                        <div className="group flex items-center font-semibold text-primary">
                            Leia mais
                            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </div>
                    </CardFooter>
                </Card>
            </Link>
        </motion.div >
    );
}

export function BlogCardSkeleton() {
    return (
        <div className="w-full sm:w-1/2 lg:w-1/3 p-3 h-full">
            <Card className="h-full flex flex-col">
                <CardHeader className="pb-2 flex-shrink-0">
                    <Skeleton className="h-4 w-16 mb-2 bg-neutral-200" />
                    <Skeleton className="h-8 w-full bg-neutral-200" />
                </CardHeader>
                <CardContent className="flex-grow">
                    <Skeleton className="h-5 w-full mb-3 bg-neutral-200" />
                    <Skeleton className="h-4 w-full mb-2 bg-neutral-200" />
                    <Skeleton className="h-4 w-3/4 mb-2 bg-neutral-200" />
                    <Skeleton className="h-4 w-5/6 bg-neutral-200" />
                </CardContent>
                <CardFooter className="mt-auto flex-shrink-0">
                    <Skeleton className="h-5 w-24 bg-neutral-200" />
                </CardFooter>
            </Card>
        </div>
    );
}